import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Switch, Tab, Tabs,
  Tooltip, Typography, LinearProgress,
} from '@mui/material'
import {
  Hub as ChannelIcon,
  ContentCopy as CopyIcon,
  Refresh as SyncIcon,
  ErrorOutline as ErrorIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { observer } from 'mobx-react-lite'
import { usePermission } from '../lib/hooks/usePermission'
import { toast } from 'react-toastify'
import { PageHeader } from '../components/shared/PageHeader'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { ChannelDrawer } from '../components/drawers/ChannelDrawer'
import { LeadMappingPanel } from '../features/channels/LeadMappingPanel'
import {
  channelsApi, leadSourcesApi, syncLogsApi,
  CHANNEL_TYPE_LABELS, CHANNEL_TYPE_COLORS,
  type Channel, type ChannelType,
} from '../lib/api/channelsApi'
import { type ChannelFormData } from '../lib/schemas/channelSchema'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`))
}

function MaskedSecret({ value }: { value: string | null }) {
  const [show, setShow] = useState(false)
  if (!value) return <Typography sx={{ fontSize: '13px', color: themeColors.text.muted }}>—</Typography>
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={{ fontSize: '13px', fontFamily: 'monospace', color: themeColors.text.primary }}>
        {show ? value : '•'.repeat(Math.min(value.length, 24))}
      </Typography>
      <Button size="small" onClick={() => setShow(s => !s)}
        sx={{ minWidth: 0, fontSize: '11px', color: themeColors.primary, textTransform: 'none', p: '2px 6px' }}>
        {show ? 'Hide' : 'Show'}
      </Button>
      <Tooltip title="Copy">
        <IconButton size="small" onClick={() => copyToClipboard(value, 'Value')}
          sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.primary } }}>
          <CopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 2, alignItems: 'center', py: 1.25,
      borderBottom: `1px solid ${themeColors.border}`, '&:last-child': { borderBottom: 'none' } }}>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: themeColors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Tab Panels
// ──────────────────────────────────────────────────────────────────────────────

function OverviewTab({ channel, onEdit, onToggle, onSync, canEdit, isSyncing, isToggling }: {
  channel: Channel
  onEdit: () => void
  onToggle: () => void
  onSync: () => void
  canEdit: boolean
  isSyncing: boolean
  isToggling: boolean
}) {
  const typeColor = CHANNEL_TYPE_COLORS[channel.type as ChannelType] ?? themeColors.primary

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' }, gap: 3 }}>
      {/* Left: Details */}
      <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${themeColors.border}`, p: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 2, color: themeColors.text.primary }}>
          Channel Details
        </Typography>

        <InfoRow label="Channel Name">
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
            {channel.name}
          </Typography>
        </InfoRow>

        <InfoRow label="Type">
          <Chip
            label={CHANNEL_TYPE_LABELS[channel.type as ChannelType]}
            size="small"
            sx={{
              height: 20, fontSize: '10.5px', fontWeight: 600,
              bgcolor: `${typeColor}18`, color: typeColor,
              border: `1px solid ${typeColor}40`,
              '& .MuiChip-label': { px: '6px' },
            }}
          />
        </InfoRow>

        <InfoRow label="Organisation">
          <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontWeight: 600 }}>
            {channel.organizationName ?? '—'}
          </Typography>
        </InfoRow>

        <InfoRow label="Line of Business">
          <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontWeight: 600 }}>
            {channel.lobName ?? '—'}
          </Typography>
        </InfoRow>

        <InfoRow label="Status">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              size="small"
              checked={channel.status === 'ACTIVE'}
              disabled={!canEdit || isToggling}
              onChange={onToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
              }}
            />
            <Typography sx={{ fontSize: '13px', fontWeight: 600,
              color: channel.status === 'ACTIVE' ? '#10b981' : themeColors.text.muted }}>
              {channel.status}
            </Typography>
          </Box>
        </InfoRow>

        {channel.type !== 'REST_API' && (
          <InfoRow label="Webhook URL">
            {channel.webhookUrl ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: themeColors.text.primary,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>
                  {channel.webhookUrl}
                </Typography>
                <Tooltip title="Copy webhook URL">
                  <IconButton size="small" onClick={() => copyToClipboard(channel.webhookUrl!, 'Webhook URL')}
                    sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.primary } }}>
                    <CopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Typography sx={{ fontSize: '13px', color: themeColors.text.muted }}>
                —
              </Typography>
            )}
          </InfoRow>
        )}

        {channel.type === 'REST_API' && (
          <>
            <InfoRow label="API Base URL">
              <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, wordBreak: 'break-all', fontWeight: 600 }}>
                {channel.apiBaseUrl ?? '—'}
              </Typography>
            </InfoRow>
            <InfoRow label="Sync Frequency">
              <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontWeight: 600 }}>
                {channel.syncFrequencyMinutes ? (
                  channel.syncFrequencyMinutes < 60
                    ? `${channel.syncFrequencyMinutes} minute${channel.syncFrequencyMinutes > 1 ? 's' : ''}`
                    : `${channel.syncFrequencyMinutes / 60} hour${channel.syncFrequencyMinutes / 60 > 1 ? 's' : ''}`
                ) : '—'}
              </Typography>
            </InfoRow>
          </>
        )}

        <InfoRow label="Access Token"><MaskedSecret value={channel.accessToken} /></InfoRow>

        <InfoRow label="Refresh Token"><MaskedSecret value={channel.refreshToken} /></InfoRow>

        <InfoRow label="Webhook Secret"><MaskedSecret value={channel.webhookSecret} /></InfoRow>

        <InfoRow label="Last Sync"><Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>{formatDateTime(channel.lastSyncAt)}</Typography></InfoRow>
        <InfoRow label="Created By">
          {channel.createdBy ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Avatar sx={{ width: 22, height: 22, fontSize: '9px', bgcolor: themeColors.primary }}>
                {channel.createdBy.name.charAt(0).toUpperCase()}
              </Avatar>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>{channel.createdBy.name}</Typography>
            </Box>
          ) : <Typography sx={{ fontSize: '13px', color: themeColors.text.muted }}>—</Typography>}
        </InfoRow>
        <InfoRow label="Created At"><Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>{formatDateTime(channel.createdAt)}</Typography></InfoRow>
        <InfoRow label="Updated At"><Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>{formatDateTime(channel.updatedAt)}</Typography></InfoRow>
      </Paper>

      {/* Right: Quick Stats */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Actions card */}
        <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${themeColors.border}`, p: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 2, color: themeColors.text.primary }}>
            Actions
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {canEdit && (
              <Button fullWidth variant="outlined" onClick={onEdit}
                sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: '8px',
                  borderColor: themeColors.primary, color: themeColors.primary }}>
                Edit Channel
              </Button>
            )}
            <Tooltip title={(channel.fieldMappings?.length ?? 0) === 0 ? "Configure Field Mapping before syncing this channel" : ""}>
              <span>
                <Button fullWidth variant="outlined"
                  disabled={channel.status !== 'ACTIVE' || isSyncing || (channel.fieldMappings?.length ?? 0) === 0}
                  onClick={onSync}
                  startIcon={isSyncing ? <CircularProgress size={14} /> : <SyncIcon />}
                  sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: '8px',
                    borderColor: themeColors.border, color: themeColors.text.secondary,
                    '&:hover': { borderColor: themeColors.primary, color: themeColors.primary },
                    '&.Mui-disabled': { borderColor: themeColors.border, color: themeColors.text.muted } }}>
                  {isSyncing ? 'Syncing…' : 'Sync Now'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>

        {/* Stats */}
        <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${themeColors.border}`, p: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 2, color: themeColors.text.primary }}>
            Stats
          </Typography>
          {[
            { label: 'Field Mappings', value: channel.fieldMappings?.length ?? 0, color: '#6366f1' },
            { label: 'Auto-Assign Rules', value: channel.autoAssignRules?.length ?? 0, color: '#f59e0b' },
          ].map(stat => (
            <Box key={stat.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              py: 1, borderBottom: `1px solid ${themeColors.border}`, '&:last-child': { borderBottom: 'none' } }}>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>{stat.label}</Typography>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: stat.color }}>{stat.value}</Typography>
            </Box>
          ))}
        </Paper>
      </Box>
    </Box>
  )
}

function LeadSourcesTab({ channel }: { channel: Channel }) {
  const qc = useQueryClient()
  const [newSourceName, setNewSourceName] = useState('')
  const [addingOpen, setAddingOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: () => leadSourcesApi.create({ name: newSourceName, channelId: channel.id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channel', channel.id] })
      toast.success('Lead source added')
      setNewSourceName('')
      setAddingOpen(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to add source'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadSourcesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channel', channel.id] })
      toast.success('Lead source removed')
      setDeletingId(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to remove source'),
  })

  const typeColor = CHANNEL_TYPE_COLORS[channel.type as ChannelType] ?? themeColors.primary
  const sources = channel.leadSources ?? []

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: themeColors.text.primary }}>
          Lead Sources ({sources.length})
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => setAddingOpen(true)}
          sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 700, borderRadius: '8px',
            bgcolor: themeColors.primary, boxShadow: 'none',
            '&:hover': { bgcolor: themeColors.primaryDark, boxShadow: 'none' } }}>
          Add Source
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {sources.length === 0 ? (
          <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: '10px',
            border: `1.5px dashed ${themeColors.border}` }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.secondary }}>
              No lead sources yet
            </Typography>
            <Typography sx={{ fontSize: '12px', color: themeColors.text.muted, mt: 0.5 }}>
              Add sources like "Facebook Lead Ads", "Website Form", "Instagram DM" etc.
            </Typography>
          </Paper>
        ) : (
          sources.map(src => (
            <Paper key={src.id} elevation={0} sx={{
              p: 1.75, borderRadius: '10px',
              border: `1px solid ${themeColors.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              '&:hover': { borderColor: typeColor, boxShadow: `0 2px 8px ${typeColor}18` },
              transition: 'all 0.15s',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: typeColor, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                  {src.name}
                </Typography>
              </Box>
              <Tooltip title="Remove source">
                <IconButton size="small" onClick={() => setDeletingId(src.id)}
                  sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fee2e2' } }}>
                  <DeleteIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Paper>
          ))
        )}
      </Box>

      {/* Add source dialog */}
      <Dialog open={addingOpen} onClose={() => setAddingOpen(false)}
        PaperProps={{ sx: { borderRadius: '12px', minWidth: 360 } }}>
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, pb: 1 }}>Add Lead Source</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <input
            autoFocus
            value={newSourceName}
            onChange={e => setNewSourceName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newSourceName.trim()) createMutation.mutate() }}
            placeholder="e.g. Facebook Lead Ads"
            style={{
              width: '100%', height: 40, padding: '0 12px',
              borderRadius: 8, border: `1px solid ${themeColors.border}`,
              fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAddingOpen(false)} size="small"
            sx={{ textTransform: 'none', color: '#6B7280' }}>Cancel</Button>
          <Button variant="contained" size="small"
            disabled={!newSourceName.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
            sx={{ textTransform: 'none', bgcolor: themeColors.primary,
              '&:hover': { bgcolor: themeColors.primaryDark } }}>
            {createMutation.isPending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteDialog
        open={!!deletingId}
        title="Remove Lead Source"
        name="this lead source"
        description="This will remove the lead source from this channel. Existing leads will not be affected."
        onClose={() => setDeletingId(null)}
        onConfirm={() => deletingId && deleteMutation.mutate(deletingId)}
        isDeleting={deleteMutation.isPending}
      />
    </Box>
  )
}

function ErrorLogsTab({ channel }: { channel: Channel }) {
  const qc = useQueryClient()

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['channel-errors', channel.id],
    queryFn: () => channelsApi.getErrorLogs(channel.id),
    staleTime: 30_000,
  })

  const clearMutation = useMutation({
    mutationFn: () => channelsApi.clearErrorLogs(channel.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channel-errors', channel.id] })
      qc.invalidateQueries({ queryKey: ['channels'] })
      toast.success('Error logs cleared')
    },
  })

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: themeColors.text.primary }}>
          Error Logs ({logs.length})
        </Typography>
        {logs.length > 0 && (
          <Button size="small" variant="outlined"
            onClick={() => clearMutation.mutate()}
            disabled={clearMutation.isPending}
            sx={{ textTransform: 'none', fontSize: '12px', color: '#dc2626',
              borderColor: '#fca5a5', '&:hover': { bgcolor: '#fee2e2', borderColor: '#dc2626' } }}>
            Clear All
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : logs.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: '10px',
          border: `1.5px dashed ${themeColors.border}` }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#10b981' }}>
            No errors — channel is healthy
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {logs.map(log => (
            <Paper key={log.id} elevation={0} sx={{
              p: 1.75, borderRadius: '10px',
              border: '1px solid #fca5a5', bgcolor: '#fff8f8',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <ErrorIcon sx={{ fontSize: 16, color: '#dc2626', flexShrink: 0, mt: '1px' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: '#dc2626', fontWeight: 500, lineHeight: 1.4 }}>
                    {log.message}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: themeColors.text.muted, mt: 0.5 }}>
                    {formatDateTime(log.createdAt)}
                  </Typography>
                  {log.payload && (
                    <Box sx={{ mt: 1, p: 1, borderRadius: '6px', bgcolor: '#fef2f2',
                      border: '1px solid #fee2e2', fontFamily: 'monospace', fontSize: '11px',
                      color: '#7f1d1d', overflowX: 'auto' }}>
                      {JSON.stringify(log.payload, null, 2)}
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  )
}

function SyncLogsTab({ channel, onSyncComplete }: { channel: Channel; onSyncComplete?: () => void }) {
  const [hasProcessing, setHasProcessing] = useState(false)
  const wasProcessing = useRef(false)

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['channel-sync-logs', channel.id],
    queryFn: async () => {
      const res = await syncLogsApi.getByChannel(channel.id)
      const processing = res.some((l: any) => l.status === 'PROCESSING')
      setHasProcessing(processing)
      return res
    },
    staleTime: hasProcessing ? 0 : 10_000,
    // Poll every 2s while a sync is PROCESSING; fall back to 30s so auto-syncs appear promptly
    refetchInterval: hasProcessing ? 2000 : 30_000,
  })

  // When PROCESSING → done, refresh the channel card so lastSyncAt / status update immediately
  useEffect(() => {
    if (wasProcessing.current && !hasProcessing) {
      onSyncComplete?.()
    }
    wasProcessing.current = hasProcessing
  }, [hasProcessing, onSyncComplete])

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '14px', color: themeColors.text.primary, mb: 2 }}>
        Sync Execution Logs ({logs.length})
      </Typography>

      {isLoading && logs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : logs.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '10px',
          border: `1.5px dashed ${themeColors.border}` }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.secondary }}>
            No sync runs executed yet
          </Typography>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.muted, mt: 0.5 }}>
            Trigger a manual sync or wait for the automatic scheduler to run.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {logs.map(log => {
            const isProcessing = log.status === 'PROCESSING'
            const statusColor = isProcessing ? '#f59e0b' : log.status === 'SUCCESS' ? '#10b981' : '#ef4444'
            return (
              <Paper key={log.id} elevation={0} sx={{
                p: 2, borderRadius: '12px',
                border: `1px solid ${isProcessing ? '#f59e0b60' : themeColors.border}`,
                boxShadow: isProcessing ? '0 0 12px rgba(245, 158, 11, 0.08)' : 'none',
                '&:hover': { boxShadow: isProcessing ? '0 0 16px rgba(245, 158, 11, 0.12)' : '0 4px 12px rgba(0,0,0,0.03)' },
                transition: 'all 0.15s'
              }}>
                {/* Header: Status & Timing */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={log.status}
                      size="small"
                      sx={{
                        height: 20, fontSize: '10px', fontWeight: 700,
                        bgcolor: `${statusColor}18`, color: statusColor,
                        border: `1px solid ${statusColor}30`,
                        '& .MuiChip-label': { px: '6px' },
                        animation: isProcessing ? 'pulse 1.5s infinite ease-in-out' : 'none',
                        '@keyframes pulse': {
                          '0%': { opacity: 0.6 },
                          '50%': { opacity: 1 },
                          '100%': { opacity: 0.6 }
                        }
                      }}
                    />
                    <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>
                      Started: {formatDateTime(log.startedAt)}
                    </Typography>
                    {log.completedAt && (
                      <Typography sx={{ fontSize: '11px', color: themeColors.text.muted }}>
                        • Duration: {Math.max(1, Math.round((new Date(log.completedAt).getTime() - new Date(log.startedAt).getTime()) / 1000))}s
                      </Typography>
                    )}
                  </Box>
                  {log.syncFrom && (
                    <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary, bgcolor: `${themeColors.background}90`, px: 1, py: 0.25, borderRadius: '4px', border: `1px solid ${themeColors.border}` }}>
                      Window: {formatDateTime(log.syncFrom)} → {formatDateTime(log.syncTo)}
                    </Typography>
                  )}
                </Box>

                {/* Progress bar for running states */}
                {isProcessing && (
                  <Box sx={{ my: 2 }}>
                    <LinearProgress 
                      variant="indeterminate" 
                      sx={{ 
                        height: 6, 
                        borderRadius: 3, 
                        bgcolor: '#f59e0b20', 
                        '& .MuiLinearProgress-bar': { 
                          bgcolor: '#f59e0b',
                          borderRadius: 3
                        } 
                      }} 
                    />
                    <Typography sx={{ fontSize: '11px', color: '#d97706', fontWeight: 600, mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#d97706', animation: 'blink 1.2s infinite' }} />
                      Sync in progress... Fetching and processing leads dynamically.
                      <style>{`
                        @keyframes blink {
                          0% { opacity: 0.3; }
                          50% { opacity: 1; }
                          100% { opacity: 0.3; }
                        }
                      `}</style>
                    </Typography>
                  </Box>
                )}

                {/* Metrics */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 2, py: 1.25, borderTop: `1px solid ${themeColors.border}`, borderBottom: log.errorMessage ? `1px solid ${themeColors.border}` : 'none' }}>
                  {[
                    { label: 'Fetched', value: log.leadsFetched, color: themeColors.primary },
                    { label: 'Imported (New)', value: log.leadsImported, color: '#10b981' },
                    { label: 'Duplicate', value: log.leadsDuplicate, color: '#6b7280' },
                    { label: 'Failed', value: log.leadsFailed, color: '#ef4444' }
                  ].map(metric => (
                    <Box key={metric.label}>
                      <Typography sx={{ fontSize: '10px', color: themeColors.text.muted, textTransform: 'uppercase', fontWeight: 600 }}>
                        {metric.label}
                      </Typography>
                      <Typography sx={{ fontSize: '16px', fontWeight: 700, color: metric.color, mt: 0.25 }}>
                        {metric.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Error message */}
                {log.errorMessage && (
                  <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '8px', bgcolor: '#fef2f2', border: '1px solid #fee2e2', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <ErrorIcon sx={{ fontSize: 16, color: '#ef4444', flexShrink: 0, mt: '1px' }} />
                    <Typography sx={{ fontSize: '12px', color: '#b91c1c', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {log.errorMessage}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Detail Page
// ══════════════════════════════════════════════════════════════════════════════

export default function ChannelDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { hasAction } = usePermission()
  const canEdit = hasAction('/channels', 'update')

  const [tab, setTab] = useState(0)
  const [editOpen, setEditOpen] = useState(false)

  const { data: channel, isLoading } = useQuery({
    queryKey: ['channel', id],
    queryFn: () => channelsApi.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  })

  const updateMutation = useMutation({
    mutationFn: (d: ChannelFormData) => channelsApi.update(id!, {
      name:                 d.name,
      type:                 d.type,
      accessToken:          d.accessToken || null,
      refreshToken:         d.refreshToken || null,
      webhookSecret:        d.webhookSecret || null,
      apiBaseUrl:           d.apiBaseUrl || null,
      syncFrequencyMinutes: d.syncFrequencyMinutes,
      fieldMappings:        d.fieldMappings,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channel', id] })
      qc.invalidateQueries({ queryKey: ['channels'] })
      setEditOpen(false)
      toast.success('Channel updated')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update'),
  })

  const toggleMutation = useMutation({
    mutationFn: () => {
      const nextStatus = channel!.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      if (nextStatus === 'ACTIVE' && (!channel!.fieldMappings || channel!.fieldMappings.length === 0)) {
        toast.error('Field mappings must be configured before activating this channel.')
        return Promise.reject(new Error('Field mappings not configured'))
      }
      return channelsApi.toggleStatus(id!, nextStatus)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channel', id] })
      qc.invalidateQueries({ queryKey: ['channels'] })
    },
  })

  const syncMutation = useMutation({
    mutationFn: () => channelsApi.sync(id!),
    onMutate: () => {
      // Switch to Sync Logs tab immediately so the user sees PROCESSING appear in real time
      setTab(2)
    },
    onSuccess: () => {
      // Invalidate sync logs so the PROCESSING entry appears without waiting for the 30s poll
      qc.invalidateQueries({ queryKey: ['channel-sync-logs', id] })
      // Also refresh channel header (status badge changes to PROCESSING)
      qc.invalidateQueries({ queryKey: ['channel', id] })
      toast.info('Sync in progress — watch the Sync Logs tab for live updates')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Sync failed'),
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!channel) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Channel not found.</Typography>
        <Button onClick={() => navigate('/channels')} sx={{ mt: 1, textTransform: 'none' }}>
          Back to Channels
        </Button>
      </Box>
    )
  }

  const typeColor = CHANNEL_TYPE_COLORS[channel.type as ChannelType] ?? themeColors.primary

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title={channel.name}
        showBreadcrumb
        breadcrumb={[{ label: 'Channel Integrations', to: '/channels' }]}
        icon={<ChannelIcon sx={{ color: typeColor }} />}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={CHANNEL_TYPE_LABELS[channel.type as ChannelType]}
              size="small"
              sx={{
                height: 24, fontSize: '11px', fontWeight: 700,
                bgcolor: `${typeColor}18`, color: typeColor,
                border: `1px solid ${typeColor}40`,
              }}
            />
            <Chip
              label={channel.status}
              size="small"
              sx={{
                height: 24, fontSize: '11px', fontWeight: 700,
                bgcolor: channel.status === 'ACTIVE' ? '#f0fdf4' : '#f8f9fa',
                color: channel.status === 'ACTIVE' ? '#15803d' : themeColors.text.muted,
                border: `1px solid ${channel.status === 'ACTIVE' ? '#bbf7d0' : themeColors.border}`,
              }}
            />
          </Box>
        }
      />

      {/* Tabs */}
      <Box sx={{ px: 3, borderBottom: `1px solid ${themeColors.border}` }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontSize: '13px', fontWeight: 500, minHeight: 40, px: 1.5 },
            '& .Mui-selected': { fontWeight: 700, color: `${themeColors.primary} !important` },
            '& .MuiTabs-indicator': { bgcolor: themeColors.primary },
          }}
        >
          <Tab label="Overview" />
          <Tab label={`Field Mapping (${channel.fieldMappings?.length ?? 0})`} />
          <Tab label="Sync Logs" />
        </Tabs>
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
        {tab === 0 && (
          <OverviewTab
            channel={channel}
            onEdit={() => setEditOpen(true)}
            onToggle={() => toggleMutation.mutate()}
            onSync={() => syncMutation.mutate()}
            canEdit={canEdit}
            isSyncing={syncMutation.isPending}
            isToggling={toggleMutation.isPending}
          />
        )}
        {tab === 1 && <LeadMappingPanel channel={channel} />}
        {tab === 2 && (
          <SyncLogsTab
            channel={channel}
            onSyncComplete={() => {
              qc.invalidateQueries({ queryKey: ['channel', id] })
            }}
          />
        )}
      </Box>

      {/* Edit drawer */}
      {canEdit && (
        <ChannelDrawer
          open={editOpen}
          mode="edit"
          onClose={() => setEditOpen(false)}
          isSubmitting={updateMutation.isPending}
          onSubmit={d => updateMutation.mutate(d)}
          initialData={{
            name:                 channel.name,
            type:                 channel.type as any,
            accessToken:          channel.accessToken ?? '',
            refreshToken:         channel.refreshToken ?? '',
            webhookSecret:        channel.webhookSecret ?? '',
            apiBaseUrl:           channel.apiBaseUrl ?? '',
            syncFrequencyMinutes: channel.syncFrequencyMinutes ?? 5,
            fieldMappings:        channel.fieldMappings ?? [],
            orgId:                channel.organizationId ?? '',
            lobId:                channel.lobId ?? '',
          }}
        />
      )}

    </Box>
  )
}
